const OPENROUTER_BASE_URL = 'https://openrouter.ai/api/v1';
const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY ?? '';

export const JURISDICTION_MODEL = 'meta-llama/llama-4-scout:nitro';
export const CONTRACT_GENERATION_MODEL = 'Claude Sonnet 4';
export const CONTRACT_GENERATION_MODEL_ID = 'anthropic/claude-sonnet-4';
export const FALLBACK_MODEL = 'openai/gpt-4o-mini';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

export interface OpenRouterMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface OpenRouterRequest {
  messages: OpenRouterMessage[];
  model?: string;
  responseFormat?: 'json' | 'text';
  temperature?: number;
  maxTokens?: number;
  signal?: AbortSignal;
}

export interface OpenRouterResponse {
  message: string;
}

// ---------------------------------------------------------------------------
// Error classification (matches old Next.js app)
// ---------------------------------------------------------------------------

function classifyError(error: unknown): { message: string; retryable: boolean } {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const s = errorMessage.toLowerCase();

  if (s.includes('json') || s.includes('parse')) {
    return { message: 'Failed to parse AI response', retryable: true };
  }
  if (s.includes('timeout') || s.includes('timed out')) {
    return { message: 'AI request timed out', retryable: true };
  }
  if (s.includes('rate limit') || s.includes('429')) {
    return { message: 'Rate limit exceeded', retryable: true };
  }
  if (s.includes('network') || s.includes('fetch') || s.includes('failed')) {
    return { message: 'Network error occurred', retryable: true };
  }
  if (s.includes('empty') || s.includes('no content')) {
    return { message: 'AI returned no content', retryable: true };
  }
  return { message: errorMessage, retryable: false };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Core API call
// ---------------------------------------------------------------------------

async function callOpenRouter(
  request: OpenRouterRequest,
  model: string
): Promise<OpenRouterResponse> {
  const {
    messages,
    responseFormat = 'text',
    temperature = 0.7,
    maxTokens = 4096,
    signal,
  } = request;

  if (!OPENROUTER_API_KEY) {
    throw new Error('OpenRouter API key is not configured (VITE_OPENROUTER_API_KEY)');
  }

  const body: Record<string, unknown> = {
    model,
    messages,
    temperature,
    max_tokens: maxTokens,
  };

  if (responseFormat === 'json') {
    body.response_format = { type: 'json_object' };
  }

  const response = await fetch(`${OPENROUTER_BASE_URL}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${OPENROUTER_API_KEY}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'Legal Templates',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`OpenRouter request failed (status ${response.status}): ${errorText}`);
  }

  const data = await response.json();
  const content = data?.choices?.[0]?.message?.content;

  if (!content) {
    throw new Error('OpenRouter returned an empty response');
  }

  return { message: content };
}

// ---------------------------------------------------------------------------
// Public API — with retry + fallback (matches old Next.js app pattern)
// ---------------------------------------------------------------------------

/**
 * Call OpenRouter with automatic retry (exponential backoff) and fallback model.
 *
 * Retry flow (same as old Next.js app):
 *   Attempt 1 → primary model
 *   Attempt 2 → primary model  (after 1 s)
 *   Attempt 3 → fallback model (after 2 s)
 */
export async function queryOpenRouter(request: OpenRouterRequest): Promise<OpenRouterResponse> {
  const primaryModel = request.model ?? JURISDICTION_MODEL;

  for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
    const modelToUse = attempt > MAX_RETRIES ? FALLBACK_MODEL : primaryModel;
    const isLastAttempt = attempt > MAX_RETRIES;

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[OpenRouter] Attempt ${attempt}/${MAX_RETRIES + 1} using model: ${modelToUse}`);
    }

    try {
      return await callOpenRouter(request, modelToUse);
    } catch (error) {
      // User-initiated abort — never retry
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      const classified = classifyError(error);

      if (import.meta.env.DEV) {
        console.error(`[OpenRouter] Attempt ${attempt} failed:`, classified.message);
      }

      if (classified.retryable && !isLastAttempt) {
        const delay = RETRY_DELAY_MS * Math.pow(2, attempt - 1);
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log(`[OpenRouter] Retrying in ${delay}ms...`);
        }
        await sleep(delay);
        continue;
      }

      throw error;
    }
  }

  // Should never reach here, but satisfy TypeScript
  throw new Error('All retry attempts exhausted');
}
